import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  DetectionResult,
  LanguageDetector,
  LanguageDetectorAvailability,
} from '@/types/language-detector';

export interface UseLanguageDetectorOptions {
  enabled?: boolean;
}

export interface UseLanguageDetectorResult {
  isSupported: boolean;
  isAvailable: boolean;
  isLoading: boolean;
  availability: LanguageDetectorAvailability | null;
  downloadProgress: number | null;
  detect: (text: string) => Promise<DetectionResult[] | null>;
  detectMostLikely: (text: string) => Promise<DetectionResult | null>;
  error: Error | null;
}

/**
 * Checks if the Language Detector API is supported in the current browser
 */
export const isLanguageDetectorSupported = (): boolean => {
  return typeof window !== 'undefined' && 'LanguageDetector' in window;
};

/**
 * Custom hook for using the Chrome Built-in AI Language Detector API
 * Handles browser support detection, detector instance lifecycle, and language detection operations
 */
export const useLanguageDetector = ({
  enabled = true,
}: UseLanguageDetectorOptions = {}): UseLanguageDetectorResult => {
  const [isSupported] = useState(() => isLanguageDetectorSupported());
  const [availability, setAvailability] =
    useState<LanguageDetectorAvailability | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const detectorRef = useRef<LanguageDetector | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const isAvailable =
    availability === 'available' ||
    availability === 'downloadable' ||
    availability === 'downloading';

  // Check availability on mount
  useEffect(() => {
    if (!isSupported || !enabled) {
      setAvailability(null);
      return;
    }

    const checkAvailability = async () => {
      try {
        const result = await window.LanguageDetector!.availability();
        setAvailability(result);
        setError(null);
      } catch (err) {
        setAvailability('unavailable');
        setError(
          err instanceof Error
            ? err
            : new Error('Failed to check availability'),
        );
      }
    };

    checkAvailability();
  }, [isSupported, enabled]);

  // Cleanup detector instance on unmount
  useEffect(() => {
    return () => {
      if (detectorRef.current) {
        detectorRef.current.destroy();
        detectorRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  /**
   * Creates or returns existing detector instance
   */
  const getDetector =
    useCallback(async (): Promise<LanguageDetector | null> => {
      if (!isSupported || !isAvailable) {
        return null;
      }

      // Return existing detector if available
      if (detectorRef.current) {
        return detectorRef.current;
      }

      try {
        setIsLoading(true);
        setDownloadProgress(null);

        // Cancel any pending creation
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        const detector = await window.LanguageDetector!.create({
          signal: abortControllerRef.current.signal,
          monitor: (monitor) => {
            monitor.addEventListener('downloadprogress', (event) => {
              const progress =
                event.total > 0 ? (event.loaded / event.total) * 100 : 0;
              setDownloadProgress(progress);
            });
          },
        });

        detectorRef.current = detector;
        setError(null);
        return detector;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return null;
        }
        setError(
          err instanceof Error
            ? err
            : new Error('Failed to create language detector'),
        );
        return null;
      } finally {
        setIsLoading(false);
        setDownloadProgress(null);
      }
    }, [isSupported, isAvailable]);

  /**
   * Detects the language of a text string
   * Returns an array of detection results sorted by confidence
   */
  const detect = useCallback(
    async (text: string): Promise<DetectionResult[] | null> => {
      if (!text.trim()) {
        return null;
      }

      const detector = await getDetector();
      if (!detector) {
        return null;
      }

      try {
        return await detector.detect(text);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error('Language detection failed'),
        );
        return null;
      }
    },
    [getDetector],
  );

  /**
   * Detects the most likely language of a text string
   * Returns only the result with highest confidence
   */
  const detectMostLikely = useCallback(
    async (text: string): Promise<DetectionResult | null> => {
      const results = await detect(text);
      if (!results || results.length === 0) {
        return null;
      }
      return results[0];
    },
    [detect],
  );

  return {
    isSupported,
    isAvailable,
    isLoading,
    availability,
    downloadProgress,
    detect,
    detectMostLikely,
    error,
  };
};
